import { FC, MouseEvent } from 'react';
import { Tooltip, Box, Typography, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { KatInput } from '@amzn/katal-react';

interface TooltipTip {
  label: string;
  text: string;
}

interface TooltipConfig {
  tips: TooltipTip[];
}

interface KatInputWithSupportProps {
  tooltipConfig?: TooltipConfig;
  supportTitle?: string;
  tooltipWidth?: number;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  // KatInput props
  label?: string;
  value?: string;
  // Note: KatInput uses custom event types (KatCustomEvent), not standard React events
  onChange?: (e: any) => void;
  onInput?: (e: any) => void;
  placeholder?: string;
  'data-testid'?: string;
  required?: boolean;
  min?: string;
  maxlength?: string;
}

const KatInputWithSupport: FC<KatInputWithSupportProps> = ({
  tooltipConfig,
  supportTitle = 'Tip(s):',
  tooltipWidth,
  tooltipPlacement = 'top',
  ...katInputProps
}) => {
  const handleCopyToClipboard = async (text: string, event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text:', err);
    } finally {
      // Update input value regardless of clipboard operation result
      if (katInputProps.onChange) {
        const syntheticEvent = {
          currentTarget: { value: text },
        };
        katInputProps.onChange(syntheticEvent);
      }
    }
  };

  if (!tooltipConfig || !tooltipConfig.tips || tooltipConfig.tips.length === 0) {
    return <KatInput {...katInputProps} />;
  }

  const tooltipContent = (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
        {supportTitle}
      </Typography>
      {tooltipConfig.tips.map((tip, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, gap: 1 }}>
          <IconButton
            size="small"
            onMouseDown={(event) => {
              handleCopyToClipboard(tip.text, event);
            }}
            sx={{ p: 0.5, color: 'inherit', flexShrink: 0 }}
            data-testid={`copy-button-${index}`}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ flex: 1 }}>
            • {tip.label}: {tip.text}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement={tooltipPlacement}
      disableInteractive={false}
      componentsProps={{
        tooltip: {
          sx: tooltipWidth ? { width: tooltipWidth, maxWidth: 'none' } : undefined,
        },
      }}
    >
      <Box tabIndex={0} data-testid="tooltip-wrapper">
        <KatInput {...katInputProps} />
      </Box>
    </Tooltip>
  );
};

export default KatInputWithSupport;
